/**
 * BarmetHub Layout — Main application shell
 * Updated for BHM-HMS-REQ-032: Setup Center button, removed activity monitor.
 */
import { Outlet, useLocation } from 'react-router-dom';
import { StartOfDayReminder, ActivityTracker } from './work/StartOfDay';
import { MailSetupBanner } from './mail/MailParts';
import WorkdayCounter from './work/WorkdayCounter';
import { usePermissionStore } from '../stores/permissionStore';
import { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import UnlockVaultDialog from './UnlockVaultDialog';
import SearchModal from './SearchModal';
import { useAuthStore } from '../stores/authStore';
import { useSecurityStore } from '../stores/securityStore';
import { useSessionGuard } from '../hooks/useSessionGuard';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useThemeStore } from '../stores/themeStore';
import { isSuperAdmin } from '../utils/permissions';
import { getConfiguracionLabel } from './setup-center/SetupCenter';
import NotificationBell from './notifications/NotificationBell';
import { useNotificationStore } from '../stores/notificationStore';

function MaterialIcon({ name, className = '' }: { name: string; className?: string }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

interface LayoutProps {
  onOpenSetup?: () => void;
}

export default function Layout({ onOpenSetup }: LayoutProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const needsSecrets = pathname.startsWith('/sistema/secrets') || /^\//.test(pathname);
  const searchUserId = useAuthStore(state => state.user?.id);
  const searchUnlocked = useAuthStore(state => Boolean(state.masterKey));
  const lock = useAuthStore((state) => state.lock);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const preference = useThemeStore((state) => state.preference);
  const setPreference = useThemeStore((state) => state.setPreference);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname]);
  const [searchOpen, setSearchOpen] = useState(false);

  const isSA = isSuperAdmin(user?.role);
  const startMentionPolling = useNotificationStore((s) => s.startMentionPolling);
  // REQ-055: notificaciones personales (menciones en comentarios de tarea).
  useEffect(() => (isAuthenticated ? startMentionPolling() : undefined), [isAuthenticated, startMentionPolling]);
  const workPerms = usePermissionStore((s) => s.permissions);

  // ⌘K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Security settings from persisted store
  const idleTimeoutMinutes = useSecurityStore((s) => s.screenLockTimeout);
  const lockOnTabHide = useSecurityStore((s) => s.screenLockOnHide);
  const showWarning = useSecurityStore((s) => s.showWarning);

  // Auto-lock on inactivity
  const guardEnabled = isAuthenticated && idleTimeoutMinutes > 0;
  useSessionGuard({
    idleTimeoutMinutes: guardEnabled ? idleTimeoutMinutes : 1440,
    warningMinutes: guardEnabled ? Math.max(idleTimeoutMinutes - 2, 1) : 1438,
    lockOnPageHide: isAuthenticated && lockOnTabHide,
    onWarning: () => {
      if (showWarning && guardEnabled) {
        toast('Tu sesión se bloqueará en 2 minutos por inactividad.', {
          icon: '⚠️',
          duration: 10000,
        });
      }
    },
    onLock: () => {
      if (guardEnabled) {
        lock();
      }
    },
  });

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <a className="hub-skip" href="#main-content">Saltar al contenido</a>
      <Sidebar collapsed={sidebarCollapsed} open={menuOpen} close={() => setMenuOpen(false)} />

      {/* Main content area */}
      <main id="main-content" style={{ marginLeft: sidebarCollapsed ? 80 : 240 }} className="hub-main flex min-h-screen flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="hub-header sticky top-0 z-40 flex min-h-[64px] items-center justify-between py-1 border-b border-outline-variant bg-background/80 px-margin-desktop backdrop-blur-md">
          <div className="flex items-center gap-4">
            <button className="md:hidden" aria-label="Abrir navegación" aria-expanded={menuOpen} onClick={() => setMenuOpen(value => !value)}>☰</button>
            <button className="hidden md:block" aria-label="Contraer navegación" aria-expanded={!sidebarCollapsed} onClick={() => setSidebarCollapsed(value => !value)}>☰</button>
            {/* Search bar */}
            <div
              className="hub-topbar-search flex max-w-full w-96 cursor-pointer items-center gap-sm rounded-full bg-surface-container-high px-4 py-2"
              onClick={() => setSearchOpen(true)}
            >
              <MaterialIcon name="search" className="text-sm text-on-surface-variant" />
              <input
                className="w-full bg-transparent text-body-md text-on-surface outline-none placeholder:text-on-surface-variant/50"
                placeholder="Buscar módulos y Secrets... (⌘K)"
                type="text"
                readOnly
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            {isAuthenticated && (isSA || workPerms.includes('TIME_VIEW_OWN') || workPerms.includes('TIME_LOG')) && <WorkdayCounter />}
            <span className="hub-desktop text-xs text-on-surface-variant">Secrets: {searchUnlocked ? 'DESBLOQUEADO' : 'BLOQUEADO'}</span>

            {/* Configuración guiada — SUPERADMIN only */}
            {isSA && (() => {
              const cached = typeof window !== 'undefined' ? localStorage.getItem('barmethub-configuracion-guiada') : null;
              const label = cached ? getConfiguracionLabel(JSON.parse(cached)) : 'Configuración guiada';
              return (
                <button
                  onClick={onOpenSetup}
                  className="hub-desktop flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#FF5E1E]/10 to-[#D946EF]/10 border border-[#FF5E1E]/20 px-3 py-1.5 text-xs font-medium text-on-surface hover:from-[#FF5E1E]/20 hover:to-[#D946EF]/20 transition-all"
                >
                  <span className="material-symbols-outlined text-sm">tune</span>
                  <span>{label}</span>
                </button>
              );
            })()}

            {/* Action buttons */}
            <div className="flex gap-2">
              <label className="sr-only" htmlFor="theme-preference">Tema visual</label>
              <select
                id="theme-preference"
                value={preference}
                onChange={(event) => setPreference(event.target.value as 'light' | 'dark' | 'system')}
                className="cursor-pointer rounded-lg border border-outline-variant bg-surface-container-high px-2 py-1 text-xs font-medium text-on-surface outline-none transition-colors hover:bg-surface-container-highest focus:ring-2 focus:ring-[#FF5E1E]"
                aria-label="Tema visual"
                title="Tema visual"
              >
                <option value="system">Sistema</option>
                <option value="light">Claro</option>
                <option value="dark">Oscuro</option>
              </select>
              <button onClick={lock} aria-label="Bloquear proyecto" title="Bloquear proyecto" className="cursor-pointer rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high">
                <MaterialIcon name="lock_open" />
              </button>
              <NotificationBell />
              <button onClick={() => navigate('/ajustes/registro')} aria-label="Ver actividad" title="Ver actividad" className="relative cursor-pointer rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high">
                <MaterialIcon name="notifications" />
              </button>
              <button onClick={() => navigate('/ajustes/mi-cuenta')} aria-label="Mi cuenta" title="Mi cuenta" className="cursor-pointer rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high">
                <MaterialIcon name="person" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto">
          <ActivityTracker enabled={isAuthenticated} />
          <MailSetupBanner enabled={isAuthenticated && !pathname.startsWith('/nexus')} />
          <StartOfDayReminder enabled={isAuthenticated && !isSA && (workPerms.includes('NEXUS_USE') || workPerms.includes('TIME_LOG'))} />
          <Outlet />
        </div>
        {needsSecrets && <UnlockVaultDialog />}
      </main>
      {searchOpen && <SearchModal key={`${searchUserId}:${searchUnlocked}`} isOpen onClose={() => setSearchOpen(false)} />}
    </div>
  );
}